// @flow

import * as React from 'react';
import cn from 'classnames';
import { type ContextRouter } from 'react-router-dom';
import createDebugger from 'debug';

import type { Thunk } from 'types/redux';
import type { Profile } from '../AdditionalInfo/AdditionalInfoForm';
import AdditionalInfo from '../AdditionalInfo/AdditionalInfo';
import SocialLoginButton from '../SocialLoginButton/SocialLoginButton';

import OAuth from 'utils/oauth/OAuth';
import facebook from 'utils/oauth/FacebookOAuth';
import google from 'utils/oauth/GoogleOAuth';
import ClientError from 'utils/errors/ClientError';

import type { SocialLoginStatus } from 'store/reducers/user/socialLoginActions';
import type { Provider } from 'store/reducers/user/social/ISocial';

// Constants
import { NAVIGATION } from 'constants/navigations';
import { USER_VEHICLES_ROUTE, VEHICLE_ROUTE } from 'constants/routes';
import { APP_FLOW } from 'constants/appFlow';

import './SocialLogin.scss';

// Types
import { gtmUserEventType, gtmStartGenericEventType } from 'types/gtm';

const { FORWARD } = NAVIGATION;
const { LOG_ME_IN } = APP_FLOW;

// mapped from the appointment reducer
type AppointmentContact = {
  firstName: string,
  lastName: string,
  phone: string,
  email: string
};

export type StateProps = {|
  /** True if this component should be visible */
  isVisible?: boolean,
  /** The country code (required for phone validation) */
  countryCode: string,
  /** The person id if it's post appointment confirmation */
  personId?: string
|};

export type Props = {|
  ...StateProps,
  /** Added to the root element (div) */
  className?: string,
  /** Performs the social login request */
  doSocialLogin: (provider: OAuth) => Thunk<Promise<SocialLoginStatus>>,
  /** See socialLoginActions.js#postSocialLogin */
  postSocialLogin: (providerName: Provider) => Thunk<void>,
  /** Sets the appointment contact info */
  setContactInfo: (contactInfo: AppointmentContact) => void,
  /** Sets the loading appointment mask */
  setLoadingAppointment: (loading: boolean) => void,
  /** Sets the current appointment flow */
  setAppFlow: (flow: string) => void,
  /** Event tracking via Google Tag Manager */
  gtmUserEvent: gtmUserEventType,
  /** Event tracking via Google Tag Manager */
  gtmStartGenericEvent: gtmStartGenericEventType,
  /**
   * Invoked when the user that attempted to do the sign up through a social
   * provider is not valid
   */
  onInvalidUser?: () => void,
  /** Invoked when a user clicks on one of the social login buttons */
  onButtonClick?: (provider: OAuth) => void,
  /** Dispatch the event to ShiftDigital when user successfully logged-in */
  sendScheduleServiceLogin: () => void,
  /** Redirection to the corresponding page after the social login */
  goTo: (path: string, direction: string) => void,
  intl: Object
|} & ContextRouter;

type State = {
  /**
   * True to show the "Need additional info" popup
   */
  needAdditionalInfo: boolean,
  /**
   * The profile retrieved from a provider, it'll be updated from the need
   * additional info popup if needed
   */
  profile: Profile | null,
  /**
   * True if the google provider is initialized
   */
  googleInitialized: boolean,
  /**
   * True if the facebook provider is initialized
   */
  facebookInitialized: boolean,
  /**
   * The provider chosen if we need additional info
   */
  provider?: OAuth
};

/**
 * Creates the facebook & google social login buttons
 */
class SocialLogin extends React.Component<Props, State> {
  static block = 'oauth-buttons';

  static defaultProps = {
    isVisible: false
  };

  // eslint-disable-next-line react/sort-comp
  log: (...any[]) => void;
  googleLogin: () => Promise<void>;
  facebookLogin: () => Promise<void>;
  facebookRef: React.Ref<'button'>;

  constructor(props: Props) {
    super(props);
    this.state = {
      needAdditionalInfo: false,
      profile: null,
      googleInitialized: google.isInitialized(),
      facebookInitialized: facebook.isInitialized()
    };
    this.log = createDebugger(`oauth:SocialLogin`);
    this.googleLogin = this.providerLogin(google);
    this.facebookLogin = this.providerLogin(facebook);
    this.facebookRef = React.createRef();
  }

  componentDidMount() {
    // register to provider changes to enable/disable the state of the buttons
    google.addListener('initialize', this.onGoogleInitialized);
    facebook.addListener('initialize', this.onFacebookInitialized);

    // facebook login workaround: if the login provider already knows that it
    // needs to do the redirection then this component should do it immediately,
    // otherwise, wait for the provider to load and inform this component if it
    // needs redirection
    this.checkFacebookRedirection();
    facebook.addListener('needsRedirection', this.checkFacebookRedirection);
  }

  componentWillUnmount() {
    google.removeListener('initialize', this.onGoogleInitialized);
    facebook.removeListener('initialize', this.onFacebookInitialized);
    facebook.removeListener('needsRedirection', this.checkFacebookRedirection);
  }

  onGoogleInitialized = () => {
    this.setState({ googleInitialized: true });
  };

  onFacebookInitialized = () => {
    this.setState({ facebookInitialized: true });
  };

  /**
   * A workaround to perform the call to xtime after the user has logged in
   * through Facebook {@see #componentDidMount}
   */
  checkFacebookRedirection = () => {
    if (facebook.needsRedirection()) {
      this.providerLoginSuccess(facebook);
    }
  };

  async providerLoginSuccess(provider: OAuth) {
    const {
      doSocialLogin,
      goTo,
      postSocialLogin,
      setAppFlow,
      setLoadingAppointment,
      sendScheduleServiceAccountCreated,
      sendScheduleServiceLogin
    } = this.props;
    const profile = await provider.getProfile();
    // attempt to do the social login
    try {
      setLoadingAppointment(true);
      await doSocialLogin(provider);
      setAppFlow(LOG_ME_IN);
      postSocialLogin(provider.getName());
      sendScheduleServiceAccountCreated();
      sendScheduleServiceLogin();
      goTo(USER_VEHICLES_ROUTE, FORWARD);
    } catch (err) {
      this.log(err);
      if (
        // babel has an issue extending builtins, see https://github.com/loganfsmyth/babel-plugin-transform-builtin-extend
        err.name === ClientError.name &&
        (err: ClientError).errorCode ===
          ClientError.errorCodes.USER_MATCH_NOT_FOUND
      ) {
        // show popup in case there's not enough info
        this.setState({
          needAdditionalInfo: true,
          provider,
          profile
        });
        return;
      } else {
        // can't recover from error
        throw err;
      }
    } finally {
      setLoadingAppointment(false);
    }
  }

  /**
   * Invoked when the user clicks on one of the social login provider buttons
   */
  providerLogin = (provider: OAuth) => async () => {
    // notification
    if (this.props.onButtonClick) {
      this.props.onButtonClick(provider);
    }

    const { gtmUserEvent } = this.props;
    await provider.login(this.props.personId);
    // only try to match if the user is valid for our app
    if (await provider.isUserValid()) {
      await this.providerLoginSuccess(provider);
    } else {
      // invalid user for our system, error bubbling & reporting
      gtmUserEvent(
        'Social Login Button',
        'Clicked',
        'Invalid User',
        'GTM_CURRENT_PAGE > Social Login',
        provider.getName()
      );
      if (this.props.onInvalidUser) {
        this.props.onInvalidUser();
      }
    }
  };

  /**
   * Invoked when the user clicks on the submit button of the additional info
   * modal, the modal is shown post provider login, it's guaranteed that profile
   * has a firstName, lastName, phoneNumber & email
   */
  onAdditionalInfoSubmit = (profile: Profile) => {
    const { setContactInfo, goTo, postSocialLogin } = this.props;
    this.toggleModal();
    setContactInfo({
      firstName: profile.firstName,
      lastName: profile.lastName,
      phone: (profile.phoneNumber: any),
      email: (profile.email: any)
    });
    if (!this.state.provider) {
      throw Error('provider must be set');
    }
    postSocialLogin(this.state.provider.getName());
    goTo(VEHICLE_ROUTE, FORWARD);
  };

  toggleModal = () => {
    this.setState({
      needAdditionalInfo: !this.state.needAdditionalInfo
    });
  };

  render() {
    const { isVisible, gtmUserEvent, gtmStartGenericEvent, intl } = this.props;

    if (!isVisible) {
      return null;
    }

    const rootClassName = cn(SocialLogin.block, this.props.className);
    const additionalInfoModalProps = {
      isOpen: this.state.needAdditionalInfo,
      toggle: this.toggleModal
    };
    return (
      <React.Fragment>
        <AdditionalInfo
          profile={this.state.profile}
          countryCode={this.props.countryCode}
          onSubmit={this.onAdditionalInfoSubmit}
          {...additionalInfoModalProps}
        />
        <div className={`${rootClassName}`}>
          <SocialLoginButton
            key="google"
            provider="google"
            disabled={!this.state.googleInitialized}
            onClick={() => {
              gtmStartGenericEvent();
              gtmUserEvent(
                'Google Button',
                'Clicked',
                'Open OAuth Account Google',
                'GTM_CURRENT_PAGE > Social Login Modal'
              );
              this.googleLogin();
            }}
          >
            {intl.formatMessage({
              id: 'c8.common.sign.in.with.google'
            })}
          </SocialLoginButton>
        </div>
      </React.Fragment>
    );
  }
}

export default SocialLogin;



// WEBPACK FOOTER //
// ./src/components/pages/User/Social/SocialLogin/SocialLogin.js